import { useEffect, useMemo, useState, useCallback, memo } from "react"
import React from "react"
import { PageProps, Link, withPrefix } from "gatsby"
import { useSelector, useDispatch } from "react-redux"

// components
import {
  Breadcrumbs,
  Props as BreadcrumbsProps
} from "../../components/Breadcrumbs/Breadcrumbs"
import { Button } from "../Button/Button"
import { Layout } from "../Layout/Layout"
import { Pill } from "../Pill/Pill"
import { SearchResultsSideNav } from "./components/SearchResultsSideNav/SearchResultsSideNav"
import { TakeOver } from "../TakeOver/TakeOver"
import { Icon } from "../Icon/Icon"
import { PaginationList } from "./components/PaginationList/PaginationList"
import { SearchBar } from "../HeaderSiteSearch/SearchBar/SearchBar"

// components
import { MicroCopy } from "../ContactForm/ContactForm"

// redux
import { setSearchQuery as setReduxSearchQuery } from "../../store/slices/search_query.slice"
import { useGetHelpArticlesByTitleQuery } from "../../services/help_articles.service"

// utils
import { useDebounce } from "../../utils/hooks/debounce.hook"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import {
  navigateWithPrefix,
  returnUrlWithLangCodePrefixOnly
} from "../../utils/general_utils"
import {
  processSearchResultsWithRelevance,
  type SearchableHit
} from "../../utils/search_relevance_utils"

// types
import type { RootState } from "../../store/store"
import { PickedLayoutProps } from "../../pages/{ContentfulHelpArticle.node_locale}/contact-us"

// constants
import { ALL_RESULTS_VALUE } from "./components/SearchResultsSideNav/SearchResultsSideNav"

// styles
import * as styles from "./SearchResultsTemplate.module.scss"
import { handleUrlString } from "../../utils/seo/seo_utils"
import {
  ArrowRightIcon,
  ChevronDownIcon,
  FilterIcon,
  Typography
} from "@eventbrite/marmalade"
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer"
import Lottie from "react-lottie"
import * as greenLoaderLottieData from "../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

type SearchPageContext = {
  node_locale?: string
}

export interface Props
  extends PageProps<Queries.SearchResultsTemplateDataQuery, SearchPageContext> {
  params: {
    lang_code: string
  }
  microCopy: SearchResultsMicroCopy
  layoutProps: PickedLayoutProps
  breadcrumbMicroCopy: BreadcrumbsProps["contentfulMicrocopy"]
  gatsbyPageProps: PageProps
}

export type CategoryTitleCounts = {
  [key: string]: number
}

export type Categories = {
  attendee: Queries.SearchResultsTemplateDataQuery["allContentfulCategoryMenu"]["nodes"]
  organizer: Queries.SearchResultsTemplateDataQuery["allContentfulCategoryMenu"]["nodes"]
}

export type SearchResultsData = {
  paginationData: {
    index: number
    value: number
    key: string
  }
  nodes: JSX.Element[]
}

export type SearchResultsMicroCopy = {
  messageSearchBar: MicroCopy
  messageSearchResultsLoading: MicroCopy
  headerSearchResults: MicroCopy
  headerNoSearchTermEntered: MicroCopy
  filterSearchAllResults: MicroCopy
  filterSearchAttendeeTopics: MicroCopy
  filterSearchOrganizerTopics: MicroCopy
  messageNoSearchResultsSubheader: MicroCopy
  buttonBrowseHelpCenter: MicroCopy
  headerNoSearchResults: MicroCopy
}

export const SearchResultsContent = memo<{
  hit: SearchableHit
}>(({ hit }) => {
  if (!hit?.fields?.helparticletitle) {
    return null
  }

  return (
    <div className={styles?.singleResultContainer}>
      <Link
        to={
          returnUrlWithLangCodePrefixOnly(
            `/articles/${hit.fields.articleid}/${hit.fields.slug}`
          ) ?? ""
        }
      >
        <h3>{hit.fields.helparticletitle}</h3>
      </Link>
      {hit.fields.summary && <p>{hit.fields.summary}</p>}
    </div>
  )
})

SearchResultsContent.displayName = "SearchResultsContent"

export const SearchResultsTopContainer = memo<{
  microCopy: SearchResultsMicroCopy
  searchQuery: string
  breadcrumbMicroCopy: BreadcrumbsProps["contentfulMicrocopy"]
  results: boolean
  nodeLocale: string
  isSearching: boolean
}>(
  ({
    searchQuery,
    microCopy,
    breadcrumbMicroCopy,
    results,
    nodeLocale,
    isSearching = false
  }) => {
    const prefixedBasePath = useMemo(
      () => withPrefix(`/${nodeLocale?.toLowerCase()}`),
      [nodeLocale]
    )

    const [isReady, setIsReady] = useState(false)

    const resultsString = useMemo(() => {
      if (isSearching) {
        return (
          <>
            {renderRichText(
              microCopy?.messageSearchResultsLoading?.raw,
              customRendererToRemovePTag
            )}{" "}
            <span>{`"${searchQuery}"`}</span>
          </>
        )
      }

      if (searchQuery?.length > 0) {
        const searchSpan = <span>{`"${searchQuery}"`}</span>

        if (results) {
          return (
            <>
              {renderRichText(
                microCopy?.headerSearchResults?.raw,
                customRendererToRemovePTag
              )}{" "}
              {searchSpan}
            </>
          )
        }

        return (
          <>
            {renderRichText(
              microCopy?.headerNoSearchResults?.raw,
              customRendererToRemovePTag
            )}{" "}
            {searchSpan}
          </>
        )
      }

      if (isReady) {
        return renderRichText(
          microCopy?.headerNoSearchTermEntered?.raw,
          customRendererToRemovePTag
        )
      }

      return ""
    }, [isSearching, searchQuery, results, isReady, microCopy])

    useEffect(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => setIsReady(true))
      } else {
        setTimeout(() => setIsReady(true), 0)
      }
    }, [])

    return (
      <div className={styles.topContainer}>
        {breadcrumbMicroCopy && (
          <div>
            <Breadcrumbs
              contentfulMicrocopy={breadcrumbMicroCopy}
              prefixedBasePath={prefixedBasePath}
              nodeLocale={nodeLocale}
            />
          </div>
        )}
        <div className={styles.searchQueryContainer}>
          <Typography as="h1" variant="heading-md">
            {resultsString}
          </Typography>
        </div>
      </div>
    )
  }
)

SearchResultsTopContainer.displayName = "SearchResultsTopContainer"

const LoadingSpinner = memo(() => {
  return (
    <Lottie
      options={{
        loop: true,
        autoplay: true,
        animationData: greenLoaderLottieData,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid slice"
        }
      }}
      width={24}
      height={24}
    />
  )
})

LoadingSpinner.displayName = "LoadingSpinner"

export const SearchResultsTemplate = memo((props: Props) => {
  const dispatch = useDispatch()

  const [isFilterTakoverActive, setIsFilterTakoverActive] = useState(false)
  const [activeFilter, setActiveFilter] = useState(ALL_RESULTS_VALUE)
  const [paginationIndex, setPaginationIndex] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)

  const searchQuery = useSelector(
    (store: RootState) => store?.searchQuery?.query
  )
  const langCode = useSelector((state: RootState) => state?.metaData?.langCode)

  // More flexible search query validation
  const shouldSkipQuery =
    typeof window === "undefined" || !searchQuery || searchQuery.length < 2

  // Always call the hook to maintain consistent hook order
  const response = useGetHelpArticlesByTitleQuery(
    { query: searchQuery || "" },
    { skip: shouldSkipQuery }
  )

  const searchResultData = response?.data

  const nodeLocale = props?.pageContext["node_locale"]

  const prefixedBasePath = useMemo(
    () => withPrefix(`/${nodeLocale?.toLowerCase()}`),
    [nodeLocale]
  )

  useEffect(() => {
    setIsSearching(response?.status === "pending")
  }, [response?.status])

  const noResultsString = useMemo<string>(() => {
    const fallback =
      "No results found. You can search again with different keywords."

    if (!props?.microCopy?.messageNoSearchResultsSubheader?.raw?.length) {
      return fallback
    }

    try {
      return documentToPlainTextString(
        JSON.parse(props.microCopy.messageNoSearchResultsSubheader.raw)
      )
    } catch {
      return fallback
    }
  }, [props?.microCopy?.messageNoSearchResultsSubheader?.raw])

  const buttonTextString = useMemo<string>(() => {
    const fallback = "Browse Help Center"

    if (!props.microCopy?.buttonBrowseHelpCenter?.raw?.length) {
      return fallback
    }

    try {
      return documentToPlainTextString(
        JSON.parse(props.microCopy.buttonBrowseHelpCenter.raw)
      )
    } catch {
      return fallback
    }
  }, [props.microCopy?.buttonBrowseHelpCenter?.raw])

  // Process search results with enhanced relevance scoring
  const processedSearchResults = useMemo(() => {
    if (!searchResultData?.result?.hit?.length || !searchQuery?.trim())
      return []

    const relevanceResults = processSearchResultsWithRelevance(
      searchResultData.result.hit,
      searchQuery
    )

    return relevanceResults
  }, [searchResultData?.result?.hit, searchQuery])

  const categoryTitleCounts = useMemo<CategoryTitleCounts>(() => {
    if (!processedSearchResults.length) return {}

    const counts = {} as CategoryTitleCounts

    // Count from the actual processed results that will be displayed
    processedSearchResults.forEach((x) => {
      const category = x?.fields?.helparticlecategory
      if (category) {
        counts[category] = (counts[category] || 0) + 1
      }
    })

    return counts
  }, [processedSearchResults])

  const split = useCallback((hitArr: SearchableHit[]): SearchableHit[][] => {
    if (!hitArr?.length) return []

    const maxLength = 10 // Maximum length for each sub-array
    const splitArray: SearchableHit[][] = []

    for (let i = 0; i < hitArr.length; i += maxLength) {
      splitArray.push(hitArr.slice(i, i + maxLength))
    }

    return splitArray
  }, [])

  const resultArray = useMemo<SearchResultsData[] | null>(() => {
    if (!searchResultData?.result?.found || !processedSearchResults.length) {
      return null
    }

    // Filter by category but maintain the relevance-based order
    const filteredHits = processedSearchResults.filter((singleHit) => {
      return (
        activeFilter === ALL_RESULTS_VALUE ||
        activeFilter === singleHit.fields.helparticlecategory
      )
    })

    const splitData = split(filteredHits)

    return splitData.map((resultArr, i) => {
      const activeList = splitData[paginationIndex] || resultArr

      const itemSet = activeList.map((x, index) => (
        <SearchResultsContent key={`${x?.id}__${i}__${index}`} hit={x} />
      ))

      return {
        paginationData: {
          key: `index__${i}`,
          index: i,
          value: i + 1
        },
        nodes: itemSet
      }
    })
  }, [
    processedSearchResults,
    activeFilter,
    paginationIndex,
    split,
    searchResultData
  ])

  const toggleFilterTakeover = useCallback(() => {
    if ((resultArray?.length ?? 0) > 0) {
      setIsFilterTakoverActive((prev) => !prev)
    }
  }, [resultArray])

  // on first load we want to check for query parameters and set that to the current search query in the SearchBar
  useEffect(() => {
    if (typeof window === "undefined") return

    const queryParams = new URLSearchParams(window.location.search)
    const q = queryParams.get("q")

    // Only restore from URL on initial component mount when URL parameter differs from Redux state
    if (q && q.length > 0 && searchQuery !== q) {
      dispatch(setReduxSearchQuery(q))
    }
  }, [dispatch])

  const debouncedSearchQuery = useDebounce(searchQuery, 200)

  useEffect(() => {
    if (typeof window === "undefined" || !debouncedSearchQuery?.length) return

    const url = new URL(window.location.href)
    const queryParams = url.searchParams

    if (
      debouncedSearchQuery.toLowerCase() !== queryParams.get("q")?.toLowerCase()
    ) {
      queryParams.set("q", debouncedSearchQuery)

      navigateWithPrefix(`/${langCode}/search?${queryParams.toString()}`, {
        replace: true
      })
    }

    setPaginationIndex(0)
    setActiveFilter(ALL_RESULTS_VALUE)
  }, [debouncedSearchQuery, langCode])

  const activeResultArray = useMemo(() => {
    if (!resultArray || paginationIndex < 0) return null
    return resultArray.length > 1
      ? resultArray[paginationIndex]?.nodes
      : resultArray[0]?.nodes
  }, [paginationIndex, resultArray])

  const showNoResults = useMemo(() => {
    return (
      isLoaded &&
      (!searchResultData?.result ||
        searchResultData.result.found === 0 ||
        !searchQuery)
    )
  }, [searchResultData, searchQuery, isLoaded])

  useEffect(() => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => setIsLoaded(true))
    } else {
      setTimeout(() => setIsLoaded(true), 0)
    }
  }, [])

  return (
    <Layout
      searchBar={{
        isSearchBarVisible: false,
        isResultsBoxShown: false
      }}
      gatsbyPageProps={props?.gatsbyPageProps}
      {...props?.layoutProps}
      contact={!isSearching}
    >
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbRow}>
          <Breadcrumbs
            contentfulMicrocopy={props?.breadcrumbMicroCopy}
            prefixedBasePath={withPrefix(`/${nodeLocale?.toLowerCase()}`)}
            nodeLocale={nodeLocale ?? ""}
          />
        </div>
        <div className={styles.centeredContent}>
          <div className={styles.resultsHeaderContainer}>
            <SearchResultsTopContainer
              nodeLocale={nodeLocale ?? ""}
              microCopy={props.microCopy}
              searchQuery={searchQuery}
              breadcrumbMicroCopy={null}
              results={(searchResultData?.result?.found ?? 0) > 0}
              isSearching={isSearching}
            />
          </div>
          <div className={styles.searchBarSection}>
            <div className={styles.searchBarContainer}>
              <SearchBar
                messageSearchBarMicrocopy={
                  props?.layoutProps?.microCopy?.messageSearchBar
                }
                wrapperClassName={styles.searchBarForm}
                takeover={{
                  enabled: false
                }}
                isResultsBoxShown={false}
                headerSearch={false}
              />
            </div>
          </div>
        </div>

        {!isSearching && (
          <div className={styles?.filterPillContainer}>
            <Pill className={styles?.pill} onClick={toggleFilterTakeover}>
              <FilterIcon className={styles?.slidersIcon} size="small" />
              Filters
              <ChevronDownIcon
                className={styles?.downChevronIcon}
                size="small"
              />
            </Pill>
          </div>
        )}

        <div className={styles.contentWrapper}>
          {(searchResultData?.result?.found ?? 0) > 0 && !showNoResults && (
            <div className={styles.sideNavContainer}>
              <SearchResultsSideNav
                searchResultData={{
                  found: processedSearchResults.length,
                  hit: processedSearchResults
                }}
                queryData={props.data}
                filterSearchAllResultsCopy={
                  props?.microCopy?.filterSearchAllResults
                }
                categoryTitleCounts={categoryTitleCounts}
                setActiveFilter={setActiveFilter}
                setPaginationIndex={setPaginationIndex}
              />
            </div>
          )}

          <div className={styles.contentContainer}>
            {(searchResultData?.result?.found ?? 0) > 0 && !showNoResults ? (
              <div className={styles.contentContainer}>{activeResultArray}</div>
            ) : showNoResults ? (
              isSearching ? (
                <div>
                  <div className={styles.loadSpinner}>
                    <LoadingSpinner />
                  </div>
                  <Link
                    to={handleUrlString(prefixedBasePath, {
                      removePrefix: true
                    })}
                  >
                    <Button
                      size="responsive"
                      variant="primary"
                      className={styles.contactButton}
                    >
                      Browse Help Center
                      <span className={styles.iconWrapper}>
                        <ArrowRightIcon size="xsmall" />
                      </span>
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <p className={styles?.noContentParagraph}>
                    {noResultsString}
                  </p>
                  <Link to={returnUrlWithLangCodePrefixOnly("/") ?? ""}>
                    <Button
                      variant="primary"
                      className={styles?.noContentButton}
                    >
                      {buttonTextString}
                      <Icon
                        className={styles?.noContentArrowIcon}
                        icon={<ArrowRightIcon size="xsmall" />}
                      />
                    </Button>
                  </Link>
                </>
              )
            ) : null}

            <div className={styles?.paginationListContainer}>
              {!showNoResults && (
                <PaginationList
                  paginationIndex={paginationIndex}
                  setPaginationIndex={setPaginationIndex}
                  data={resultArray ?? []}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <TakeOver
        isSearchTakeOverOpen={isFilterTakoverActive}
        toggleTakeOver={toggleFilterTakeover}
        headerTitle="Filters"
      >
        <div className={styles.takeoverFilterContainer}>
          <SearchResultsSideNav
            searchResultData={{
              found: processedSearchResults.length,
              hit: processedSearchResults
            }}
            queryData={props.data}
            categoryTitleCounts={categoryTitleCounts}
            filterSearchAllResultsCopy={
              props?.microCopy?.filterSearchAllResults
            }
            setActiveFilter={setActiveFilter}
            rightOrientedRadios={true}
            setPaginationIndex={setPaginationIndex}
          />
        </div>

        <div className={styles?.takeoverFilterFooter}>
          <Button onClick={toggleFilterTakeover}>Apply filters</Button>
        </div>
      </TakeOver>
    </Layout>
  )
})

SearchResultsTemplate.displayName = "SearchResultsTemplate"
